import type {SourceSceneGenerationRequest} from './source-scene-generation';
import type {NativeOutputFormat} from './output-format';
import type { NativeEditorEnvelope, NativeWorkScope } from "./native-envelope";
import type {CompanyContinueClip} from './continue-clip';
import type {CompanySpeechClip,CompanySpeechSettings} from './speech-clip';
import type {CompanyVoiceClip} from './voice-replacement';
import type {CompanyLibrarySource} from './library-source';
import type {VoiceDraftInput,CompanyVoiceDraft} from './voice-draft';
import type {VideoDraftInput,CompanyVideoDraft} from './video-draft';
import type {VideoGenerationReceipt} from './video-generation-receipt';
import type {SceneVoiceDraft,SceneVoiceAttachment} from './scene-voice';

/** The port grants access to one already authorized work, never a general API or an auth token. */
export const COMPANY_WORK_CHANNEL = "waka-company-work-v1";
export type CompanyWorkSeed = {
  scope: NativeWorkScope;
  revision: string;
  sourceSnapshotHash: string | null;
  file: NativeEditorEnvelope | null;
  legacyDocument: unknown;
  sourceContext: Record<string, unknown>;
};
export type CompanySaveResult =
  | { ok: true; revision: string; file: NativeEditorEnvelope; output?: {ok:boolean;message:string;browser?:{width?:number;height?:number;outputFormat?:NativeOutputFormat}} }
  | { ok: false; message: string };
export type CompanySaveIntent='render'|'browser-render'|{type:'continue'|'speech'|'voice';clipId:string}|{type:'voice-draft';draft:VoiceDraftInput}|{type:'scene-voice';draft:SceneVoiceDraft}|{type:'video-draft';draft:VideoDraftInput}|{type:'video-source';kind:'upload'|'library'};
export type CompanySaveWork = (file:NativeEditorEnvelope,intent?:CompanySaveIntent)=>Promise<CompanySaveResult>;

export type CompanyUploadedMedia = {
  id:string; filename:string; mimeType:string; size:number;
  remoteUrl:string; remoteFileKey:string|null; blob:Blob;
  continueFrom?:CompanyContinueClip;
  attachmentId?:string;
  speech?:{source:CompanySpeechClip|CompanyVoiceClip;settings:CompanySpeechSettings};
  librarySource?:CompanyLibrarySource;
  voiceDraft?:CompanyVoiceDraft;
  sceneVoice?:SceneVoiceAttachment;
  videoDraft?:CompanyVideoDraft;
  sourceSceneGeneration?:SourceSceneGenerationRequest;
  sourceSceneRevision?:string;
  videoGeneration?:VideoGenerationReceipt;
};
export type CompanyMediaAdded={assetId:string};
export type SubscribeUploadedMedia = (receive:(media:CompanyUploadedMedia)=>Promise<CompanyMediaAdded|void>)=>()=>void;

/** Only first-party hosts (and loopback for local QA) may hand a work to the editor. Anything else never connects. */
const COMPANY_HOST_ORIGINS=new Set(['https://studio.crabs.ai','https://dev.crabs.ai']);
export function trustedCompanyHostOrigin(raw:string|null):string|null{
  if(!raw)return null;
  try{
    const url=new URL(raw);
    if(url.origin!==raw)return null;
    if(COMPANY_HOST_ORIGINS.has(url.origin))return url.origin;
    return url.protocol==='http:'&&['127.0.0.1','localhost'].includes(url.hostname)?url.origin:null;
  }catch{return null;}
}

export function companyEditorUrl(raw: string, hostOrigin: string): URL | null {
  try {
    const url = new URL(raw);
    const local =
      url.protocol === "http:" &&
      ["127.0.0.1", "localhost"].includes(url.hostname);
    if (
      (!local && url.protocol !== "https:") ||
      url.username ||
      url.password ||
      url.origin === hostOrigin
    )
      return null;
    url.search = "";
    url.hash = "";
    url.searchParams.set("hostOrigin", hostOrigin);
    return url;
  } catch {
    return null;
  }
}

/** Host-run services the editor may offer. A host that sends no flag keeps every tool as before. */
export type CompanyEditorServices={captionTranslation:boolean;aiScene:boolean;aiSceneModify:boolean};
export function readCompanyEditorServices(raw:unknown):CompanyEditorServices{
  const value=raw&&typeof raw==='object'?raw as Record<string,unknown>:{};
  return {captionTranslation:value.captionTranslation!==false,aiScene:value.aiScene!==false,aiSceneModify:value.aiSceneModify!==false};
}

export type CompanyBrowserOutput = (input:{blob:Blob;revision:string})=>Promise<string>;
export type SubscribeBrowserOutputAction=(receive:(action:'render'|'publish')=>void)=>()=>void;
